import React, { useState } from 'react';
import { apiUrl } from '../config';

interface PasswordResetModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userEmail: string;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

export default function PasswordResetModal({
  isOpen,
  onClose,
  userId,
  userEmail,
  onSuccess,
  onError,
}: PasswordResetModalProps) {
  const [passwordOption, setPasswordOption] = useState<'temporary' | 'permanent' | 'generated'>(
    'temporary'
  );
  const [customPassword, setCustomPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handlePasswordReset = async () => {
    if (!userId) return;
    setLoading(true);

    try {
      const res = await fetch(`${apiUrl('users')}/${encodeURIComponent(userId)}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          passwordOption,
          customPassword: customPassword || undefined,
        }),
      });

      if (!res.ok) throw new Error(await res.text());

      const result = await res.json();
      const successMessage = `✅ Password reset successfully${result.password ? ` - New password: ${result.password}` : ''}`;

      onSuccess?.(successMessage);
      onClose();
    } catch (err: any) {
      const errorMessage = `❌ Failed to reset password. ${err?.message ? `(${err.message})` : ''}`;
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPasswordOption('temporary');
    setCustomPassword('');
    setLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
      <div className='bg-slate-800 p-6 rounded-lg shadow-xl max-w-md w-full mx-4'>
        <h3 className='text-xl font-semibold text-slate-100 mb-4'>Reset Password</h3>
        <p className='text-slate-300 mb-4'>Choose how to reset the password for {userEmail}:</p>

        <div className='space-y-3 mb-4'>
          <label className='flex items-center'>
            <input
              type='radio'
              name='passwordOption'
              value='temporary'
              checked={passwordOption === 'temporary'}
              onChange={e =>
                setPasswordOption(e.target.value as 'temporary' | 'permanent' | 'generated')
              }
              className='mr-2 w-4 h-4 text-blue-600 bg-slate-700 border-white/10 focus:ring-blue-500 focus:ring-2'
            />
            <span className='text-sm text-slate-200'>
              Temporary Password (User must change on first login)
            </span>
          </label>

          <label className='flex items-center'>
            <input
              type='radio'
              name='passwordOption'
              value='permanent'
              checked={passwordOption === 'permanent'}
              onChange={e =>
                setPasswordOption(e.target.value as 'temporary' | 'permanent' | 'generated')
              }
              className='mr-2 w-4 h-4 text-blue-600 bg-slate-700 border-white/10 focus:ring-blue-500 focus:ring-2'
            />
            <span className='text-sm text-slate-200'>Permanent Password (No reset required)</span>
          </label>

          <label className='flex items-center'>
            <input
              type='radio'
              name='passwordOption'
              value='generated'
              checked={passwordOption === 'generated'}
              onChange={e =>
                setPasswordOption(e.target.value as 'temporary' | 'permanent' | 'generated')
              }
              className='mr-2 w-4 h-4 text-blue-600 bg-slate-700 border-white/10 focus:ring-blue-500 focus:ring-2'
            />
            <span className='text-sm text-slate-200'>System Generated Password</span>
          </label>
        </div>

        {(passwordOption === 'temporary' || passwordOption === 'permanent') && (
          <div className='mb-4'>
            <label className='block text-sm font-medium text-slate-200 mb-2'>
              Custom Password (Optional)
            </label>
            <input
              type='password'
              placeholder='Leave empty to auto-generate'
              value={customPassword}
              onChange={e => setCustomPassword(e.target.value)}
              className='w-full px-3 py-2 bg-slate-700 border border-white/10 rounded text-slate-100 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none'
            />
            <p className='text-xs text-slate-400 mt-1'>
              If left empty, a secure password will be generated automatically
            </p>
          </div>
        )}

        <div className='flex justify-end space-x-2'>
          <button
            onClick={handleClose}
            className='px-4 py-2 border border-white/10 rounded text-slate-200 hover:bg-white/10 transition-colors'
          >
            Cancel
          </button>
          <button
            onClick={handlePasswordReset}
            disabled={loading}
            className='bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 transition-colors'
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </div>
      </div>
    </div>
  );
}
